import React from "react";
import { FLAG } from "../../constants/matches";


// ── Icônes ───────────────────────────────────────────────────────────────────
const TICKET_ICON = "https://i.postimg.cc/d3sX5KSp/ticket-foot-viol-(1).png";
const COIN_ICON = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/CoinIcon.png";
const POINT_ICON = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/PointIcon%20(1).png";

export interface BetResultData {
  t1: string;
  t2: string;
  realScore: string;
  predictedScore: string | null;
  isWin: boolean;
  isExact: boolean;
  pointsEarned: number;
  goldEarned: number;
  gotTicket: boolean;
  consolationPts?: number; // points de consolation sur une défaite
}

interface Props {
  items: BetResultData[];
  onClose: () => void;
}

const MAX_W = 460; // largeur mobile — on bloque le contenu dessus sur desktop

// Fond commun aux 2 écrans : noir 0A0708 @70% + blur 42px
const OVERLAY_BG: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 200,
  background: "rgba(10,7,8,0.70)",
  backdropFilter: "blur(42px)",
  WebkitBackdropFilter: "blur(42px)",
};

// ─────────────────────────────────────────────────────────────────────────────
//  Carte d'un résultat
// ─────────────────────────────────────────────────────────────────────────────
function ResultCard({ d }: { d: BetResultData }) {
  const isExact = d.isExact;
  const isWin = d.isWin;
  const [sa, sb] = d.realScore.split(/[–-]/).map((x) => Number(x.trim()));
  const isDraw = !isNaN(sa) && !isNaN(sb) && sa === sb;
  const t1Wins = !isDraw && sa > sb;
  const t2Wins = !isDraw && sb > sa;
  const nameOpacity = (won: boolean) => (isDraw ? 1 : won ? 1 : 0.6);
  const flagOpacity = (won: boolean) => (isDraw ? 1 : won ? 1 : 0.5);

  // Couleurs par état
  let topStroke = "rgba(255,255,255,0.30)"; // défaite
  let gradient = "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.20), rgba(255,255,255,0.06))";
  if (isExact) {
    topStroke = "rgba(255,232,177,0.50)";
    gradient = "radial-gradient(circle at 50% 0%, rgba(255,175,62,0.24), rgba(255,255,255,0.06))";
  } else if (isWin) {
    topStroke = "rgba(72,255,133,0.30)";
    gradient = "radial-gradient(circle at 50% 0%, rgba(72,255,133,0.20), rgba(255,255,255,0.06))";
  }

  return (
    <div
      style={{
        position: "relative",
        borderRadius: 12,
        background: gradient,
        padding: 14,
        overflow: "hidden",
      }}
    >
      {/* contour haut uniquement */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: topStroke }} />

{/* Gains : coin haut-droit */}
      {isWin && (
        <div style={{ position: "absolute", top: 14, right: 14, display: "flex", alignItems: "center", gap: 5 }}>
          <img src={POINT_ICON} alt="" style={{ width: 17, height: 17, objectFit: "contain", transform: "rotate(-11.06deg)" }} />
          <img src={COIN_ICON} alt="" style={{ width: 17, height: 17, objectFit: "contain", marginLeft: -10 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: "#FFEFE0" }}>{d.pointsEarned}</span>
        </div>
      )}

      {/* 1. Icône résultat centrée (Désormais EN HAUT) */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12, marginTop: 4 }}>
        <ResultIcon isExact={isExact} isWin={isWin} />
      </div>

{/* Drapeaux superposés (Écartés de 3px supplémentaires) */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          
          {/* Drapeau 1 */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.80)",
              zIndex: 2, 
              position: "relative",
              fontSize: 24,
            }}
          >
            <span style={{ opacity: flagOpacity(t1Wins) }}>{FLAG[d.t1] || "🏳️"}</span>
          </div>

          {/* Drapeau 2 */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.80)",
              zIndex: 1,
              position: "relative",
              marginLeft: -6, // On réduit le chevauchement pour écarter les drapeaux
              fontSize: 24,
              // Le centre du masque est recalibré pile sur le premier drapeau à -17px
              WebkitMask: "radial-gradient(circle 25px at -17px 50%, transparent 98%, black 100%)",
              mask: "radial-gradient(circle 25px at -17px 50%, transparent 98%, black 100%)",
            }}
          >
            <span style={{ opacity: flagOpacity(t2Wins) }}>{FLAG[d.t2] || "🏳️"}</span>
          </div>

        </div>
      </div>
      {/* Noms d'équipes */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          marginBottom: 10,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        <span style={{ opacity: nameOpacity(t1Wins) }}>{d.t1}</span>
        <span style={{ fontWeight: 400, opacity: 0.6 }}>vs</span>
        <span style={{ opacity: nameOpacity(t2Wins) }}>{d.t2}</span>
      </div>

      {/* Bloc score */}
      <div
        style={{
          position: "relative",
          background: "rgba(7,7,7,0.4415)",
          borderRadius: 6,
          padding: "10px 14px",
          minHeight: 22,
        }}
      >
        {/* score réel centré */}
        <div style={{ textAlign: "center", fontSize: 28, fontWeight: 800, letterSpacing: "0.53em", paddingLeft: "0.53em" }}>
          <span style={{ opacity: nameOpacity(t1Wins) }}>{isNaN(sa) ? "?" : sa}</span>
          <span style={{ opacity: 0.4 }}>-</span>
          <span style={{ opacity: nameOpacity(t2Wins) }}>{isNaN(sb) ? "?" : sb}</span>
        </div>
        {/* prono du joueur à droite */}
        {d.predictedScore && (
          <span
            style={{
              position: "absolute",
              right: 14,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 12,
              color: "rgba(255, 255, 255, 0.5)",
            }}
          >
            Me : {d.predictedScore}
          </span>
        )}
      </div>
    </div>
  );
}

function ResultIcon({ isExact, isWin }: { isExact: boolean; isWin: boolean }) {
  if (isExact) {
    return (
      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "radial-gradient(circle at 35% 30%, #FFDF93, #FFBA4A)", display: "grid", placeItems: "center" }}>
        <Check />
      </div>
    );
  }
  if (isWin) {
    return (
      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#48FF85", display: "grid", placeItems: "center" }}>
        <Check dark />
      </div>
    );
  }
  return (
    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.4)", display: "grid", placeItems: "center" }}>
      <Cross />
    </div>
  );
}

function Check({ dark }: { dark?: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l4 4L19 7" stroke={dark ? "#05140A" : "#000000"} strokeWidth="3.2" strokeLinecap="square" strokeLinejoin="round" />
    </svg>
  );
}

function Cross() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M6 6l12 12M18 6L6 18" stroke="#000000" strokeWidth="3.2" strokeLinecap="square" />
    </svg>
  );
}

// CTA fixe en bas avec z-index et fondu dégradé
function StickyCTA({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div 
      style={{ 
        position: "absolute", 
        left: 0, 
        right: 0, 
        bottom: 0, 
        padding: "48px 20px 20px", // Plus de padding en haut pour lisser le dégradé
        display: "flex", 
        justifyContent: "center",
        zIndex: 10, // Règle le problème de superposition au scroll
        // Dégradé qui passe de transparent à la couleur de fond
        // J'ai mis une base noire rgba(10,10,10, x), adapte la couleur selon ton vrai fond
        background: "linear-gradient(to bottom, rgba(10,10,10,0) 0%, rgba(10,10,10,0.9) 40%, rgba(10,10,10,1) 100%)",
        pointerEvents: "none" // Le bloc conteneur ne bloque pas les clics sur la dernière carte
      }}
    >
      <button
        onClick={onClick}
        style={{
          width: "100%",
          maxWidth: MAX_W,
          background: "#48FF85",
          color: "#05140A",
          border: "none",
          borderRadius: 14,
          padding: "16px 0",
          fontSize: 15,
          fontWeight: 800,
          cursor: "pointer",
          pointerEvents: "auto" // On réactive les clics spécifiquement sur le bouton
        }}
      >
        {label}
      </button>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
//  Écran 1 — Liste des résultats
// ─────────────────────────────────────────────────────────────────────────────
function ResultsScreen({ items, onContinue }: { items: BetResultData[]; onContinue: () => void }) {
  return (
    <div style={{ ...OVERLAY_BG, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "48px 20px 110px" }}>
        <div style={{ maxWidth: MAX_W, margin: "0 auto" }}>
          {/* En-tête */}
          <div style={{ textAlign: "center", marginBottom: 22 }}>
            <div style={{ fontSize: 50, marginBottom: 4 }}>🔮</div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#48FF85",
                letterSpacing: "0.38em",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              {items.length} {items.length > 1 ? "matches" : "match"}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#fff", letterSpacing: "-0.015em" }}>
              Pronostics Results
            </div>
          </div>

          {/* Cartes */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {items.map((d, i) => (
              <ResultCard key={i} d={d} />
            ))}
          </div>
        </div>
      </div>

      <StickyCTA label="Continue" onClick={onContinue} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Écran 2 — Récompenses agrégées
// ─────────────────────────────────────────────────────────────────────────────
function RewardItem({
  icon,
  amount,
  contour,
  glow,
  delay,
}: {
  icon: React.ReactNode;
  amount: number;
  contour: string;
  glow: string;
  delay: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        animation: `rewardPop 0.32s ${delay}ms cubic-bezier(0.34,1.56,0.64,1) both`,
      }}
    >
      <div style={{ position: "relative" }}>
        {/* lueur ellipse 25% */}
        <div
          style={{
            position: "absolute",
            inset: -10,
            borderRadius: "50%",
            background: `radial-gradient(ellipse at center, ${glow} 0%, transparent 70%)`,
            opacity: 0.25,
          }}
        />
        <div
          style={{
            position: "relative",
            width: 82,
            height: 82,
            borderRadius: 10,
            border: `1px solid ${contour}`,
            display: "grid",
            placeItems: "center",
          }}
        >
          {icon}
        </div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", marginTop: 8 }}>+{amount}</div>
    </div>
  );
}

function RewardsScreen({ items, onAccept }: { items: BetResultData[]; onAccept: () => void }) {
  const totalPts = items.reduce((s, d) => s + (d.pointsEarned || 0) + (d.consolationPts || 0), 0);
  const totalGold = items.reduce((s, d) => s + (d.goldEarned || 0), 0);
  const totalTickets = items.reduce((s, d) => s + (d.gotTicket ? 1 : 0), 0);

  return (
    <div style={{ ...OVERLAY_BG, display: "flex", flexDirection: "column" }}>
      <style>{`@keyframes rewardPop{from{opacity:0;transform:scale(0.6) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 28 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 32 }}>Your rewards</div>

        <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
          <RewardItem icon={<img src={POINT_ICON} alt="" style={{ width: 44, height: 44, objectFit: "contain" }} />} amount={totalPts} contour="#48FF85" glow="#1AFF66" delay={0} />
          <RewardItem icon={<img src={COIN_ICON} alt="" style={{ width: 44, height: 44, objectFit: "contain" }} />} amount={totalGold} contour="#FFE677" glow="#FFB700" delay={90} />
          {totalTickets > 0 && (
            <RewardItem
              icon={<img src={TICKET_ICON} alt="" style={{ width: 44, height: 44, objectFit: "contain" }} />}
              amount={totalTickets}
              contour="#EFBAFF"
              glow="#8120FF"
              delay={180}
            />
          )}
        </div>
      </div>

      <StickyCTA label="Accept" onClick={onAccept} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Conteneur 2 phases
// ─────────────────────────────────────────────────────────────────────────────
export function BetResultScreen({ items, onClose }: Props) {
  const [phase, setPhase] = React.useState<"results" | "rewards">("results");
  if (!items.length) return null;

  return phase === "results" ? (
    <ResultsScreen items={items} onContinue={() => setPhase("rewards")} />
  ) : (
    <RewardsScreen items={items} onAccept={onClose} />
  );
}
